// src/components/modals/ConfirmModal.tsx
import React from "react";
import { Download } from "lucide-react";

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  icon?: React.ReactNode;
  iconBgColor?: string;
  confirmButtonColor?: string;
  imageCount: number;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Continue",
  cancelText = "Cancel",
  icon,
  iconBgColor = "bg-blue-100",
  confirmButtonColor = "bg-blue-600 hover:bg-blue-700",
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
        <div className="flex items-start gap-4">
          <div
            className={`flex-shrink-0 w-12 h-12 ${iconBgColor} rounded-full flex items-center justify-center`}
          >
            {icon || <Download className="h-6 w-6 text-blue-600" />}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {title}
            </h3>
            <p className="text-sm text-gray-600 mb-4">{message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${confirmButtonColor}`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// USAGE EXAMPLES:

// 1. Confirm Modal
/*
<ConfirmModal
  isOpen={showConfirmModal}
  onClose={() => setShowConfirmModal(false)}
  onConfirm={handleConfirm}
  title="Download Multiple Images?"
  message="This will download 50 images individually. Your browser may ask for permission."
  confirmText="Continue"
  cancelText="Cancel"
/>
*/

// 2. Success Modal
/*
<SuccessModal
  isOpen={showSuccessModal}
  onClose={() => setShowSuccessModal(false)}
  title="Download Started!"
  message="Started downloading 50 images. Please check your downloads folder."
/>
*/

// 3. Alert Modal (with different types)
/*
<AlertModal
  isOpen={showAlert}
  onClose={() => setShowAlert(false)}
  type="success" // or "error", "warning", "info"
  title="Success!"
  message="Operation completed successfully."
/>

<AlertModal
  isOpen={showError}
  onClose={() => setShowError(false)}
  type="error"
  title="Error Occurred"
  message="Something went wrong. Please try again."
/>

<AlertModal
  isOpen={showWarning}
  onClose={() => setShowWarning(false)}
  type="warning"
  title="Warning"
  message="This action cannot be undone."
/>
*/
